import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Save, Loader2, ExternalLink, Mail } from "lucide-react";
import { api, errorMessage, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StoreRatings } from "./StoreRatings";

const BASE = "/admin/podcast-finder";

// Slide-over with the prospect's details, editable contact + notes, and every email we've sent them.
export const ProspectDrawer = ({ prospect, onClose, onSaved }) => {
  const [notes, setNotes] = useState(prospect?.notes || "");
  const [email, setEmail] = useState(prospect?.email || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setNotes(prospect?.notes || ""); setEmail(prospect?.email || ""); }, [prospect?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!prospect) return null;
  const dirty = notes !== (prospect.notes || "") || email !== (prospect.email || "");
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.patch(`${BASE}/prospects/${prospect.id}`, { notes, email }); onSaved(data); toast.success("Saved"); }
    catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };
  const thread = [...(prospect.emails || [])].reverse();
  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="prospect-drawer">
      <aside className="h-full w-full max-w-xl overflow-y-auto bg-[#0A1730] border-l border-white/10 p-6 space-y-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          {prospect.image ? <img src={prospect.image} alt="" className="h-14 w-14 rounded-xl object-cover" /> : <div className="h-14 w-14 rounded-xl bg-[#053671]" />}
          <div className="min-w-0 flex-1">
            <p className="font-display text-xl font-black leading-tight" data-testid="prospect-drawer-name">{prospect.name}</p>
            <p className="text-sm text-slate-400 truncate">{prospect.author}{prospect.genres?.length ? ` · ${prospect.genres.slice(0, 3).join(", ")}` : ""}</p>
            <p className="text-xs text-slate-500 mt-1">{prospect.episodes} episodes · last {prospect.last_episode_at ? formatDate(prospect.last_episode_at) : "—"} · found via “{prospect.source}”{prospect.apple_url && <> · <a href={prospect.apple_url} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline inline-flex items-center gap-0.5">Apple Podcasts <ExternalLink size={10} /></a></>}{prospect.spotify_url && <> · <a href={prospect.spotify_url} target="_blank" rel="noreferrer" className="text-emerald-300 hover:underline inline-flex items-center gap-0.5" data-testid="prospect-drawer-spotify">Spotify <ExternalLink size={10} /></a></>}</p>
            <div className="mt-1.5" data-testid="prospect-drawer-ratings"><StoreRatings p={prospect} /></div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-slate-400 hover:text-white" aria-label="Close" data-testid="prospect-drawer-close"><X size={18} /></button>
        </div>
        {prospect.description && <p className="text-sm text-slate-300 leading-relaxed" data-testid="prospect-drawer-description">{prospect.description}</p>}
        {prospect.reason && <p className="text-xs text-amber-300">Filtered: {prospect.reason}</p>}

        <div>
          <label className="text-sm font-medium">Contact email</label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="No email found in the feed — paste one if you track it down" className="mt-1.5 bg-white/5 border-white/10 font-mono text-xs" data-testid="prospect-drawer-email" />
        </div>
        <div>
          <label className="text-sm font-medium">Notes <span className="text-slate-500 font-normal">(only you see these)</span></label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Spoke to the host on X, interested after the season…" className="mt-1.5 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm leading-relaxed" data-testid="prospect-drawer-notes" />
        </div>
        <div className="flex justify-end">
          <Button type="button" onClick={save} disabled={!dirty || saving} className="rounded-full bg-sky-400 text-[#070F1E] font-bold hover:bg-sky-300" data-testid="prospect-drawer-save">{saving ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Save size={15} className="mr-2" />} Save</Button>
        </div>

        <div>
          <p className="eyebrow mb-2 flex items-center gap-1.5"><Mail size={12} /> Email thread · {thread.length}</p>
          {thread.length === 0 ? <p className="text-sm text-slate-500" data-testid="prospect-thread-empty">Nothing sent yet.</p> : (
            <ol className="space-y-3" data-testid="prospect-thread">
              {thread.map((m, i) => (
                <li key={`${m.at}-${m.type}`} className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid="prospect-thread-item">
                  <div className="flex items-center justify-between gap-3 text-[11px] text-slate-400"><span className={`rounded-full px-2 py-0.5 font-bold ${m.type === "followup" ? "bg-indigo-400/15 text-indigo-300" : "bg-emerald-400/15 text-emerald-300"}`}>{m.type === "followup" ? "Follow-up" : "Intro"}</span><span>{formatDate(m.at)} · to {m.to}</span></div>
                  <p className="mt-2 text-sm font-semibold">{m.subject}</p>
                  {m.body && <p className="mt-2 whitespace-pre-line text-sm text-slate-300 leading-relaxed">{m.body}</p>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </aside>
    </div>
  );
};
